import { useState } from 'react'
import { toast } from 'sonner'
import { User, OvertimeRequest } from '@/types'
import { OvertimeStatus, UserRole } from '@/types/enums'
import { supabase } from '@/integrations/supabase/client'
import { v4 as uuidv4 } from 'uuid'
import { 
  calculateMonthlyOvertimeHours, 
  getCompanyOvertimeThreshold
} from '@/utils/overtimeUtils'
import { format as formatDate } from 'date-fns'

/**
 * Hook for handling overtime request submission logic.
 * Provides functions for submitting, preparing, and managing overtime requests.
 */

export interface OvertimeRequestFormData {
  date: string;
  hours: string;
  startTime?: string;
  endTime?: string;
  justification?: string;
}

export function useSubmissionHandlers(
  user: User | null,
  employees: User[],
  _overtimeRequests: OvertimeRequest[],
  setOvertimeRequests: React.Dispatch<React.SetStateAction<OvertimeRequest[]>>
) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitOvertimeRequest = async (
    employeeId: string,
    requestData: OvertimeRequestFormData
  ): Promise<boolean> => {
    if (!user) {
      // Critical error: user must be logged in
      return false
    }
    try {
      setIsSubmitting(true)
      const employee = employees.find(e => e.id === employeeId)
      if (!employee) {
        // Critical error: employee must be found
        return false;
      }
      const companyId = employee.companyId || user.companyId;
      if (!companyId) {
        // Critical error: company ID must be found
        return false;
      }
      const threshold = await getCompanyOvertimeThreshold(companyId)
      const { date, hours } = requestData;
      const requestHours = parseFloat(hours || '0')
      const totalMonthlyHours = await calculateMonthlyOvertimeHours(
        employeeId,
        date,
        true,
        requestHours
      )
      // Check if VP approval is required based on dynamic threshold
      const requiresVpApproval = totalMonthlyHours > threshold;
      const requestStatus = user.role === UserRole.SUPER_ADMIN ? OvertimeStatus.APPROVED : 
                           (user.role === UserRole.SUPERINTENDENT ? 
                             (requiresVpApproval ? OvertimeStatus.PENDING_VP_APPROVAL : OvertimeStatus.PENDING_DH_APPROVAL) : OvertimeStatus.PENDING)
      const formattedDate = date ? formatDate(new Date(date), 'yyyy-MM-dd') : formatDate(new Date(), 'yyyy-MM-dd');
      const { startTime, endTime, justification } = requestData;
      const newRequest: OvertimeRequest = {
        id: uuidv4(),
        employee_id: employeeId,
        employee_name: employee.name || 'Unknown',
        department: employee.department || 'Uncategorized',
        date: formattedDate, 
        hours: isNaN(requestHours) ? 0 : requestHours,
        start_time: startTime || null,
        end_time: endTime || null,
        justification: justification || '',
        status: requestStatus,
        submitted_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        vp_approval_required: requiresVpApproval,
        approver_id: null,
        approver_name: null,
        approver_comment: null
      }
      const { data, error } = await supabase
        .from('overtime_requests')
        .insert({
          id: newRequest.id,
          employee_id: newRequest.employee_id,
          employee_name: newRequest.employee_name,
          department: newRequest.department,
          date: newRequest.date,
          hours: newRequest.hours,
          start_time: newRequest.start_time,
          end_time: newRequest.end_time,
          justification: newRequest.justification,
          status: newRequest.status,
          submitted_at: newRequest.submitted_at,
          updated_at: newRequest.updated_at,
          vp_approval_required: newRequest.vp_approval_required,
          created_by_name: user.name || null,
          created_by_role: user.role || null
        } as any)
        .select();
      if (error) {
        // Critical error: submission failed
        toast.error(`Submission failed: ${error.message}`);
        throw error; 
      }
      if (data && data.length > 0) {
        setOvertimeRequests(prev => [...prev, newRequest]);
      } else {
        toast.error('Submission confirmation failed. Please check status.');
        return false; 
      }
      // Only show success message if critical
      toast.success('Request sent successfully', { id: `submit-success-${newRequest.id}`, duration: 1500 });
      return true;
    } catch {
      toast.error('Failed to submit overtime request. Please try again.', { duration: 1500 });
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // You may restore other handlers here as needed

  return {
    submitOvertimeRequest,
    isSubmitting,
    // ...other handlers
  };
}


